<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { call } from '../api/http'
import { ledger, type Account } from '../api/ledger'
import { useSession } from '../stores/session'
import { compact, useForm } from '../lib/form'
import { formatMoney, isValidIban } from '../lib/money'
import UiCard from '../components/UiCard.vue'
import UiButton from '../components/UiButton.vue'
import UiBadge from '../components/UiBadge.vue'
import UiField from '../components/UiField.vue'
import ErrorBanner from '../components/ErrorBanner.vue'
import EmptyState from '../components/EmptyState.vue'

const { t, locale } = useI18n()
const session = useSession()
const load = useForm()
const create = useForm()
const action = useForm()

const accounts = ref<Account[]>([])
const showCreate = ref(false)
const form = reactive({ name: '', iban: '', currency: 'EUR', bank: '' })
const editing = ref<string | null>(null)
const edit = reactive({ name: '', bank: '' })

const ibanHint = computed(() => (form.iban && !isValidIban(form.iban) ? t('accounts.ibanInvalid') : ''))

async function reload() {
  await load.submit(async () => (accounts.value = await call(ledger.GET('/accounts'))))
}
watch(() => session.workspace?.id, reload, { immediate: true })

async function onCreate() {
  const ok = await create.submit(async () => {
    const a = await call(ledger.POST('/accounts', { body: compact({ ...form, currency: form.currency.toUpperCase() }) }))
    accounts.value = [...accounts.value, a]
  })
  if (ok) {
    Object.assign(form, { name: '', iban: '', bank: '' })
    showCreate.value = false
  }
}

function startEdit(a: Account) {
  editing.value = a.id
  Object.assign(edit, { name: a.name, bank: a.bank ?? '' })
}

const patch = (a: Account, body: { name?: string; bank?: string; is_active?: boolean }) =>
  action.submit(async () => {
    Object.assign(a, await call(ledger.PATCH('/accounts/{id}', { params: { path: { id: a.id } }, body })))
    editing.value = null
  })

async function remove(a: Account) {
  if (!confirm(t('accounts.deleteConfirm', { name: a.name }))) return
  await action.submit(async () => {
    await call(ledger.DELETE('/accounts/{id}', { params: { path: { id: a.id } } }))
    accounts.value = accounts.value.filter((x) => x.id !== a.id)
  })
}
</script>

<template>
  <div class="page">
    <header class="page-header">
      <div>
        <h1>{{ $t('accounts.title') }}</h1>
        <p class="muted">{{ $t('accounts.subtitle') }}</p>
      </div>
      <UiButton v-if="session.canWrite && !showCreate" data-testid="new-account" @click="showCreate = true">{{ $t('accounts.new') }}</UiButton>
    </header>

    <ErrorBanner :error="action.error.value ?? load.error.value" />

    <UiCard v-if="showCreate" :title="$t('accounts.new')">
      <form class="grid-2" data-testid="create-account" @submit.prevent="onCreate">
        <UiField :label="$t('accounts.name')" :error="create.fieldErr('name')">
          <input v-model.trim="form.name" name="name" required maxlength="100" />
        </UiField>
        <UiField :label="$t('legal.iban')" :error="create.fieldErr('iban')" :hint="ibanHint">
          <input v-model.trim="form.iban" name="iban" maxlength="42" autocomplete="off" :aria-invalid="!!ibanHint" />
        </UiField>
        <UiField :label="$t('accounts.currency')" :error="create.fieldErr('currency')">
          <input v-model.trim="form.currency" name="currency" required pattern="[A-Za-z]{3}" maxlength="3" />
        </UiField>
        <UiField :label="$t('legal.bankName')" :error="create.fieldErr('bank')">
          <input v-model.trim="form.bank" name="bank" maxlength="100" />
        </UiField>
        <div class="row">
          <UiButton type="submit" :loading="create.busy.value">{{ $t('common.create') }}</UiButton>
          <UiButton variant="ghost" @click="showCreate = false">{{ $t('common.cancel') }}</UiButton>
        </div>
      </form>
      <ErrorBanner :error="create.error.value" />
    </UiCard>

    <UiCard>
      <p v-if="load.busy.value && !accounts.length" class="muted">{{ $t('common.loading') }}</p>
      <EmptyState v-else-if="!accounts.length" :title="$t('accounts.empty')" :message="$t('accounts.emptyText')" />
      <ul v-else class="list" data-testid="account-list">
        <li v-for="a in accounts" :key="a.id" :class="{ inactive: !a.is_active }">
          <form v-if="editing === a.id" class="row edit" @submit.prevent="patch(a, { name: edit.name, bank: edit.bank })">
            <input v-model.trim="edit.name" :aria-label="$t('accounts.name')" required maxlength="100" />
            <input v-model.trim="edit.bank" :aria-label="$t('legal.bankName')" :placeholder="$t('legal.bankName')" maxlength="100" />
            <UiButton type="submit" size="sm" :loading="action.busy.value">{{ $t('common.save') }}</UiButton>
            <UiButton size="sm" variant="ghost" @click="editing = null">{{ $t('common.cancel') }}</UiButton>
          </form>
          <template v-else>
            <div class="stack" style="gap: 0.1rem; flex: 1; min-width: 12rem">
              <strong>
                <RouterLink :to="{ path: '/transactions', query: { account_id: a.id } }">{{ a.name }}</RouterLink>
                <UiBadge v-if="!a.is_active" variant="outline">{{ $t('accounts.inactive') }}</UiBadge>
              </strong>
              <span class="muted small">
                {{ [a.bank, a.iban].filter(Boolean).join(' · ') || a.currency }} ·
                {{ $t('accounts.txCount', { n: a.tx_count }) }}
              </span>
            </div>
            <strong class="balance">{{ formatMoney(a.balance, locale) }}</strong>
            <div v-if="session.canWrite" class="row" style="gap: 0.25rem">
              <UiButton size="sm" variant="ghost" @click="startEdit(a)">{{ $t('common.edit') }}</UiButton>
              <UiButton size="sm" variant="ghost" :disabled="action.busy.value" @click="patch(a, { is_active: !a.is_active })">
                {{ a.is_active ? $t('accounts.deactivate') : $t('accounts.activate') }}
              </UiButton>
              <UiButton v-if="a.tx_count === 0" size="sm" variant="ghost" :disabled="action.busy.value" @click="remove(a)">
                {{ $t('common.delete') }}
              </UiButton>
            </div>
          </template>
        </li>
      </ul>
    </UiCard>
  </div>
</template>

<style scoped>
.balance {
  font-variant-numeric: tabular-nums;
}
.inactive {
  opacity: 0.6;
}
.edit {
  flex: 1;
}
.edit input {
  flex: 1;
  min-width: 10rem;
}
</style>
